import { Embroidery, EmbroideryKey, EmbroideryObjects, PartialActionDispatch, State } from 'src/types'
import { isHalfWidthCharChecker } from 'src/logic'
import { useEmbroideryContent } from 'src/hooks/useEmbroideryContent'
import { useEmbroideriesDispatcher } from 'src/hooks/useEmbroideriesDispatcher'

import {
  typeFaceObjs,
  positionObjs,
  colorObjs,
  shadowEdgeColorObjs,
  typeFaceEnObjs,
  typeFaceJaObjs
} from 'src/features/yellStory/constants/embroidery'

export const positionObjectsGenerator = (
  leatherIntegratedMiddleValue: string,
  leatherIntegratedRingValue: string,
  ysrDirectLogo1PositionValue: string,
  ysrDirectLogo2PositionValue: string
) => {
  const isOptionedLeatherIntegratedMiddle = leatherIntegratedMiddleValue !== 'none'
  const isOptionedLeatherIntegratedRing = leatherIntegratedRingValue !== 'none'
  const isOptionedMiddleYsrDirectLogo1 = ['ringFinger'].includes(ysrDirectLogo1PositionValue)
  const isOptionedMiddleYsrDirectLogo2 = ['middleFinger'].includes(ysrDirectLogo2PositionValue)
  const isOptionedRingYsrDirectLogo1 = ['ringFinger'].includes(ysrDirectLogo1PositionValue)
  const isOptionedRingYsrDirectLogo2 = ['ringFinger'].includes(ysrDirectLogo2PositionValue)
  const positionObjects = [
    ...positionObjs,
    {
      label: '中指部分',
      value: 'middleFinger',
      order: 1,
      isSelectable: isOptionedLeatherIntegratedMiddle && !isOptionedMiddleYsrDirectLogo1 && !isOptionedMiddleYsrDirectLogo2
    },
    {
      label: '薬指部分',
      value: 'ringFinger',
      order: 2,
      isSelectable: isOptionedLeatherIntegratedRing && !isOptionedRingYsrDirectLogo1 && !isOptionedRingYsrDirectLogo2
    }
  ]
    .filter((z) => z.isSelectable)
    .filter((z) => ![ysrDirectLogo1PositionValue, ysrDirectLogo2PositionValue].includes(z.value))
    .sort((x, y) => x.order - y.order)
  return positionObjects
}

export const useYsrEmbroideriesDispatchGenerator = (dispatch: PartialActionDispatch, state: State) => {
  const { embroideries, leatherIntegratedMiddle, leatherIntegratedRing, ysrDirectLogo1Position, ysrDirectLogo2Position } = state
  const positionObjects = positionObjectsGenerator(
    leatherIntegratedMiddle.value,
    leatherIntegratedRing.value,
    ysrDirectLogo1Position.value,
    ysrDirectLogo2Position.value
  )
  const ysrObjects: EmbroideryObjects = {
    typeFace: typeFaceObjs,
    position: positionObjects,
    color: colorObjs,
    shadowColor: shadowEdgeColorObjs,
    edgeColor: shadowEdgeColorObjs,
    pointColor: shadowEdgeColorObjs
  }
  const ysrEmbroideriesDispatcher = (dispatch: PartialActionDispatch, embroideries: Embroidery[], key: EmbroideryKey) => {
    return (selected: string, i?: number) => useEmbroideriesDispatcher(dispatch, embroideries, selected, ysrObjects, key, i)
  }
  const handleContentAndNum = useEmbroideryContent(dispatch, embroideries)
  return {
    handle: {
      typeFace: ysrEmbroideriesDispatcher(dispatch, embroideries, 'typeFace'),
      position: ysrEmbroideriesDispatcher(dispatch, embroideries, 'position'),
      color: ysrEmbroideriesDispatcher(dispatch, embroideries, 'color'),
      shadowColor: ysrEmbroideriesDispatcher(dispatch, embroideries, 'shadowColor'),
      edgeColor: ysrEmbroideriesDispatcher(dispatch, embroideries, 'edgeColor'),
      pointColor: ysrEmbroideriesDispatcher(dispatch, embroideries, 'pointColor'),
      content: (selected: string, i?: number) => {
        if (i === undefined) return
        handleContentAndNum(selected, i)
      }
    }
  }
}

export const characterCheckHelper = (contentStr: string) => {
  const isHalfWidthChar = isHalfWidthCharChecker(contentStr)
  const isJapanese = contentStr.charCodeAt(0) >= 256
  const typeFaceObjects = isJapanese ? typeFaceJaObjs : typeFaceEnObjs
  return {
    contentMaxLength: isHalfWidthChar ? 12 : 6,
    existsContent: contentStr.length > 0,
    typeFaceObjects
  }
}

const typeFaceResolver = (embroidery: Embroidery) => {
  const typeFace = typeFaceObjs.find((t) => t.label === embroidery.typeFace.label) || typeFaceObjs[0]
  return typeFace
}

const typeDecorationResolver = (embroidery: Embroidery) => {
  if (embroidery.shadowColor.value !== 'none') {
    return { label: 'カゲ', path: 'shadow' }
  }
  if (embroidery.edgeColor.value !== 'none') {
    return { label: 'フチ', path: 'edge' }
  }
  return { label: 'シングル', path: 'single' }
}

export const fontImageResolver = (embroidery: Embroidery) => {
  const typeFace = typeFaceResolver(embroidery)
  const typeDecoration = typeDecorationResolver(embroidery)
  const combination = `${typeFace.label} x ${typeDecoration.label}`
  return { typeFace, typeDecoration, combination }
}
